// ADR 23.9.26u §6–§7, 16.10.26b §1–§2 — the one service-worker registration,
// and the update store the toast reads.
//
// **Hand-written, not `virtual:pwa-register`.** The virtual module registers
// inside the plugin's generated code, so a source walk for `register(` would
// find nothing anywhere and 23.9.26u AC4 would pass for the wrong reason; and a
// module that takes its `ServiceWorkerContainer` as a parameter is tested with
// a fake rather than a `vi.mock` of a virtual id.
//
// **The page never reloads under a learner who has started** (roadmap D2, as
// narrowed by ADR 16.10.26b §2). A reload mid-exercise or mid-tutor-turn loses
// the learner's work; before their first pointer or key event there is nothing
// to lose. So:
//
// - Only a page that *started* controlled has an update (16.10.26b §1): an
//   uncontrolled page runs the network shell, so a first worker taking it over,
//   or one found waiting, replaces nothing.
// - A waiting worker (left by an earlier visit, or reaching `installed` under a
//   controller) found before any input is activated quietly (`SKIP_WAITING`)
//   and the page reloads once on the `controllerchange`; after input it flags
//   `available` for the toast.
// - `applyUpdate()` posts `SKIP_WAITING`; `reload()` runs on the
//   `controllerchange` that follows it. A `controllerchange` this tab did not
//   ask for (another tab accepted) follows the same input rule; that tab's
//   "Reload" then just reloads, since no worker is left waiting.
// - At most one quiet reload per tab per minute, stamped in `sessionStorage`,
//   so an engine that fires an unrequested `controllerchange` on every load
//   costs one reload and then the toast, never a loop.
// - A long-lived standalone app checks `sw.js` when it becomes visible, at
//   most hourly. Checking is not applying.
//
// The one call site is `main.tsx`, under `import.meta.env.PROD` (AC6).
//
// ADR 25.9.26q §4 — **`register` waits for window `load`.** The worker's
// install precaches the wasm, and on a first visit that 642 kB competes with
// the entry, CSS and locale for the same link (Workbox's own guidance). The
// `controllerchange` listener is still attached at once, so an update landing
// mid-load is not missed. The "on `load`, or now if complete" logic is written
// out here rather than imported from `wasm/preloadHint.ts`: it has no idle
// step, and `pwa/` depending on `wasm/` would invert the layering.

/** The browser seams, injected so tests hand in fakes. */
export interface RegisterDeps {
  nav: Pick<Navigator, 'serviceWorker'> | undefined
  doc: Pick<Document, 'visibilityState' | 'addEventListener' | 'readyState'>
  /** The window whose `load` gates `register` (25.9.26q §4). */
  win: Pick<Window, 'addEventListener'>
  /** `location.reload` in `main.tsx`; a spy in tests. */
  reload: () => void
  /** The hourly `update()` throttle's and the quiet-reload guard's clock. */
  now: () => number
  /** `window.sessionStorage`, behind a getter: touching it throws where storage is blocked (16.10.26b §2). */
  session: () => Pick<Storage, 'getItem' | 'setItem'>
}

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000
const QUIET_RELOAD_KEY = 'lingara.pwa.quiet_reload_at'
const QUIET_RELOAD_INTERVAL_MS = 60 * 1000

let available = false
/** Who posted `SKIP_WAITING`: the learner's Reload, or the quiet path (16.10.26b §2). */
let applying: 'learner' | 'quiet' | null = null
let interacted = false
let startedControlled = false
let registration: ServiceWorkerRegistration | null = null
let reload: (() => void) | null = null
let now: (() => number) | null = null
let session: RegisterDeps['session'] | null = null
const listeners = new Set<() => void>()

function setAvailable(): void {
  if (available) return
  available = true
  for (const listener of listeners) listener()
}

/** True when a quiet reload happened under a minute ago, or storage is blocked. */
function quietReloadSpent(): boolean {
  try {
    const stamp = session?.().getItem(QUIET_RELOAD_KEY) ?? null
    // A missing or unparseable stamp is unspent: `Number(null)` is 0, which a
    // clock starting at 0 would read as "just now".
    if (stamp === null) return false
    const at = Number(stamp)
    if (stamp.trim() === '' || !Number.isFinite(at)) return false
    return (now?.() ?? 0) - at < QUIET_RELOAD_INTERVAL_MS
  } catch {
    // Blocked storage: no guard can be kept, so the toast it is.
    return true
  }
}

function spendQuietReload(): void {
  try {
    session?.().setItem(QUIET_RELOAD_KEY, String(now?.() ?? 0))
  } catch {
    // Unreachable in practice: `quietReloadSpent` already read the same storage.
  }
}

/** A waiting worker: before any input, activate it quietly; after, offer it. */
function onWaiting(worker: ServiceWorker): void {
  if (interacted || quietReloadSpent()) {
    setAvailable()
    return
  }
  applying = 'quiet'
  worker.postMessage({ type: 'SKIP_WAITING' })
}

/** A new controller this learner did not ask for: before any input, reload once; after, offer it. */
function onNewController(): void {
  if (interacted || quietReloadSpent()) {
    setAvailable()
    return
  }
  spendQuietReload()
  reload?.()
}

/** A new worker that reaches `installed` while this page is controlled is an update. */
function watchInstalling(reg: ServiceWorkerRegistration, container: ServiceWorkerContainer): void {
  reg.addEventListener('updatefound', () => {
    const worker = reg.installing
    if (worker === null) return
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && container.controller !== null) onWaiting(worker)
    })
  })
}

/** §7 — `registration.update()` on becoming visible, at most once an hour. */
function watchVisibility(reg: ServiceWorkerRegistration, deps: RegisterDeps): void {
  let lastCheck = deps.now()
  deps.doc.addEventListener('visibilitychange', () => {
    if (deps.doc.visibilityState !== 'visible') return
    const now = deps.now()
    if (now - lastCheck < UPDATE_CHECK_INTERVAL_MS) return
    lastCheck = now
    reg.update().catch(() => undefined)
  })
}

function onRegistered(reg: ServiceWorkerRegistration, container: ServiceWorkerContainer, deps: RegisterDeps): void {
  registration = reg
  // Left waiting by an earlier visit: the update is already here. An
  // uncontrolled page (a shift-reload) leaves it for the next controlled load.
  if (reg.waiting !== null && startedControlled) onWaiting(reg.waiting)
  watchInstalling(reg, container)
  watchVisibility(reg, deps)
}

/** Register `/sw.js` and start watching for updates; a no-op without `serviceWorker`. */
export function registerServiceWorker(deps: RegisterDeps): void {
  const nav = deps.nav
  // Absent in Firefox private windows and some embedded browsers.
  if (nav?.serviceWorker === undefined) return
  const container = nav.serviceWorker
  reload = deps.reload
  now = deps.now
  session = deps.session
  startedControlled = container.controller !== null
  container.addEventListener('controllerchange', () => {
    if (applying === 'learner') deps.reload()
    // A quiet apply's own activation, or another tab's: the same rule decides.
    // A page that started uncontrolled replaces nothing (16.10.26b §1).
    else if (applying === 'quiet' || startedControlled) onNewController()
  })
  // Capture phase, so the flag is set before any app handler runs; attached
  // before `load` so input during the load counts (16.10.26b §2).
  const markInteracted = () => {
    interacted = true
  }
  deps.win.addEventListener('pointerdown', markInteracted, { capture: true, once: true })
  deps.win.addEventListener('keydown', markInteracted, { capture: true, once: true })
  const register = () => {
    // Spelled `nav.serviceWorker.register` on purpose: 23.9.26u AC4's source walk
    // looks for exactly that shape, and finds it here and nowhere else.
    nav.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => onRegistered(reg, container, deps))
      // A refused registration leaves the app exactly as it was without a worker.
      .catch(() => undefined)
  }
  if (deps.doc.readyState === 'complete') register()
  else deps.win.addEventListener('load', register, { once: true })
}

/** For `useSyncExternalStore`. */
export function subscribeUpdate(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function updateAvailable(): boolean {
  return available
}

/** The learner's "Reload": skip the waiting worker, and reload once it controls. */
export function applyUpdate(): void {
  const waiting = registration?.waiting ?? null
  if (waiting === null) {
    // Another tab already activated it: this tab is controlled by the new
    // worker and only its in-memory shell is old.
    reload?.()
    return
  }
  applying = 'learner'
  waiting.postMessage({ type: 'SKIP_WAITING' })
}

/** Tests only: the store is module-level. */
export function resetForTests(): void {
  available = false
  applying = null
  interacted = false
  startedControlled = false
  registration = null
  reload = null
  now = null
  session = null
  listeners.clear()
}
